import { eq, and, desc, or, sql, type SQL } from "drizzle-orm";
import { DrizzleD1Database } from "drizzle-orm/d1";
import { activityLogs } from "../schemas/activity-logs";
import { users } from "../schemas/users";
import { generateUlid, createOnlyTimestamps } from "@kubdee/shared";
import { resolveActivityTargetAliases } from "./activity-target-aliases";

// ============================================================================
// Types
// ============================================================================

export type ActivityAction =
  | "created"
  | "updated"
  | "deleted"
  | "state_changed"
  | "commented"
  | "call"
  | "email"
  | "note"
  | "meeting"
  | "submitted"
  | "approved"
  | "rejected"
  | "paid"
  | "payment_received"
  | "payment_cancelled"
  | "approval_requested"
  | "approval_approved"
  | "approval_rejected"
  // Audit actions (#279) — tax-report exports + document prints. Write only.
  | "exported"
  | "printed"
  // Operator-visible runtime warnings (#456) — surface zero-COGS / dirty-data
  // hints in the Activity feed so admins can act on them. Distinct from
  // "rejected" (which implies workflow refusal); "warning" is informational.
  | "warning";

export interface FieldChange {
  field: string;
  old: string | number | null;
  new: string | number | null;
}

export interface LogActivityParams {
  module: string;
  doctype: string;
  document_id: string;
  action: ActivityAction;
  summary?: string;
  description?: string | null;
  changes?: FieldChange[];
  user_id: string;
}

export interface GetActivitiesParams {
  module: string;
  doctype: string;
  document_id: string;
  page?: number;
  limit?: number;
}

// ============================================================================
// logActivity — Insert activity log entry
// ============================================================================

export async function logActivity(
  db: DrizzleD1Database,
  params: LogActivityParams
) {
  const id = generateUlid();
  const timestamps = createOnlyTimestamps(params.user_id);

  await db.insert(activityLogs).values({
    id,
    module: params.module,
    doctype: params.doctype,
    documentId: params.document_id,
    action: params.action,
    summary: params.summary || null,
    description: params.description || null,
    changes: params.changes ? JSON.stringify(params.changes) : null,
    userId: params.user_id,
    ...timestamps,
  });

  return { id, createdAt: timestamps.createdAt };
}

// ============================================================================
// logStateChange — Record a workflow state transition
// ============================================================================

export interface LogStateChangeParams {
  module: string;
  doctype: string;
  document_id: string;
  /** Thai noun for this doctype (e.g. "การจอง", "ใบเสนอราคา"). */
  doctypeLabel: string;
  /** Record identifier (e.g. bookingNo, quotationNo). */
  recordLabel: string;
  oldState: string | null;
  newState: string;
  user_id: string;
}

/**
 * Log a workflow state transition. Produces the same format every caller
 * would otherwise reinvent — "{doctypeLabel} {recordLabel}: {oldState} → {newState}"
 * — and also records the field change so the UI can render a translated
 * "สถานะ: <old> → <new>" below the summary if needed.
 */
export async function logStateChange(
  db: DrizzleD1Database,
  params: LogStateChangeParams
) {
  const summary =
    `${params.doctypeLabel} ${params.recordLabel}: ${params.oldState ?? ""} → ${params.newState}`.trim();

  return logActivity(db, {
    module: params.module,
    doctype: params.doctype,
    document_id: params.document_id,
    action: "state_changed",
    summary,
    changes: [
      {
        field: "workflow_state",
        old: params.oldState,
        new: params.newState,
      },
    ],
    user_id: params.user_id,
  });
}

// ============================================================================
// computeChanges — Diff two records and return field changes
// ============================================================================

const DEFAULT_SKIP_FIELDS = new Set([
  "id", "createdAt", "createdBy", "updatedAt", "updatedBy",
  "deletedAt", "deletedBy",
]);

export function computeChanges(
  existing: Record<string, any>,
  updated: Record<string, any>,
  opts?: { skipFields?: string[]; labelMap?: Record<string, string> }
): FieldChange[] | undefined {
  const skip = opts?.skipFields
    ? new Set([...DEFAULT_SKIP_FIELDS, ...opts.skipFields])
    : DEFAULT_SKIP_FIELDS;
  const labelMap = opts?.labelMap ?? {};

  const changes: FieldChange[] = [];
  for (const key of Object.keys(updated)) {
    if (skip.has(key)) continue;
    const oldVal = existing[key] ?? null;
    const newVal = updated[key] ?? null;
    if (String(oldVal) !== String(newVal)) {
      changes.push({
        field: labelMap[key] || key,
        old: oldVal as string | number | null,
        new: newVal as string | number | null,
      });
    }
  }
  return changes.length > 0 ? changes : undefined;
}

// ============================================================================
// getActivities — Get activity timeline with pagination
// ============================================================================

export async function getActivities(
  db: DrizzleD1Database,
  params: GetActivitiesParams
) {
  const page = params.page ?? 1;
  const limit = params.limit ?? 20;
  const offset = (page - 1) * limit;
  const whereClause = activityLookupWhere(params);

  // Get total count
  const countResult = await db
    .select({ count: sql<number>`count(*)` })
    .from(activityLogs)
    .where(whereClause)
    .get();

  const total = countResult?.count ?? 0;

  // Get activities with user info
  const activities = await db
    .select({
      id: activityLogs.id,
      module: activityLogs.module,
      doctype: activityLogs.doctype,
      documentId: activityLogs.documentId,
      action: activityLogs.action,
      summary: activityLogs.summary,
      description: activityLogs.description,
      changes: activityLogs.changes,
      userId: activityLogs.userId,
      createdAt: activityLogs.createdAt,
      userName: users.name,
      userEmail: users.email,
    })
    .from(activityLogs)
    .leftJoin(users, eq(activityLogs.userId, users.id))
    .where(whereClause)
    // Tiebreaker on id (ULID → monotonic-ish): two logActivity calls inside the
    // same request can land on the exact same ms timestamp, so createdAt alone
    // gives SQLite a non-deterministic sort (e.g. auto-submit's created vs
    // state_changed flips between refreshes).
    .orderBy(desc(activityLogs.createdAt), desc(activityLogs.id))
    .limit(limit)
    .offset(offset)
    .all();

  // Parse changes JSON
  const parsed = activities.map((a) => ({
    ...a,
    changes: a.changes ? JSON.parse(a.changes) : null,
  }));

  return {
    data: parsed,
    meta: {
      page,
      perPage: limit,
      total,
    },
  };
}

function activityLookupWhere(params: GetActivitiesParams): SQL {
  const targetPredicate = activityTargetPredicate(params.module, params.doctype);
  return and(eq(activityLogs.documentId, params.document_id), targetPredicate) as SQL;
}

function activityTargetPredicate(module: string, doctype: string): SQL {
  const targets = resolveActivityTargetAliases({ module, doctype });
  const clauses = targets
    .map((target) => and(
      eq(activityLogs.module, target.module),
      eq(activityLogs.doctype, target.doctype),
    ))
    .filter((clause): clause is SQL => Boolean(clause));

  if (clauses.length === 1) return clauses[0]!;
  return or(...clauses) as SQL;
}
